import { useCallback } from 'react';

interface GoogleUser {
    credential: string;
    select_by: string;
}

interface UseGoogleAuthProps {
    onSuccess: (credential: string) => void;
    onError: (error: any) => void;
}

declare global {
    interface Window {
        google?: {
            accounts: {
                id: {
                    initialize: (config: any) => void;
                    prompt: () => void;
                    renderButton: (element: HTMLElement, config: any) => void;
                    disableAutoSelect: () => void;
                };
            };
        };
    }
}

export const useGoogleAuth = ({ onSuccess, onError }: UseGoogleAuthProps) => {
    const initializeGoogleAuth = useCallback(() => {
        if (!window.google) {
            console.error('Google Identity Services not loaded');
            return;
        }

        window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: (response: GoogleUser) => {
                if (response.credential) {
                    onSuccess(response.credential);
                } else {
                    onError(new Error('No credential received from Google'));
                }
            },
            auto_select: false,
            cancel_on_tap_outside: true,
            use_fedcm: false, // Disable FedCM to avoid dev-mode throttling/cooldowns
        });
    }, [onSuccess, onError]);

    const signInWithGoogle = useCallback(() => {
        if (!window.google) {
            onError(new Error('Google Identity Services not loaded'));
            return;
        }

        try {
            window.google.accounts.id.prompt();
        } catch (error) {
            onError(error);
        }
    }, [onError]);

    const renderGoogleButton = useCallback((element: HTMLElement, theme: 'outline' | 'filled_blue' | 'filled_black' = 'outline', isDarkMode: boolean = false) => {
        if (!window.google || !element) {
            return;
        }

        // Clear any existing button
        element.innerHTML = '';

        window.google.accounts.id.renderButton(element, {
            theme: 'outline', // Use outline theme - CSS will handle dark mode styling
            size: 'large',
            type: 'standard',
            shape: 'rectangular',
            text: 'signin_with',
            logo_alignment: 'left',
            width: 400,
        });
    }, []);

    return {
        initializeGoogleAuth,
        signInWithGoogle,
        renderGoogleButton,
    };
};
